import type { ClassifyTuple } from '@/services/classification.service'

/** A resolved classification: axis code → selected value code. */
export type ClassificationTuple = Record<string, string>

/** Per-file overrides win over the group-wide batch tuple, axis by axis. */
export function effectiveClassification(
	batch: ClassificationTuple,
	override: ClassificationTuple = {},
): ClassificationTuple {
	return { ...batch, ...override }
}

export interface Readiness {
	ready: boolean
	/** Required dimensions with no resolved value; empty when `ready`. */
	missing: string[]
}

/** A file is ready once every required dimension has a resolved value. */
export function readiness(effective: ClassificationTuple, required: string[]): Readiness {
	const missing = required.filter((code) => !effective[code])
	return { ready: missing.length === 0, missing }
}

/**
 * Flatten ready files into the flat tuple array the batch classify endpoint
 * expects — one tuple per resolved axis value per file. Held-back files are
 * excluded by the caller (only ready files are passed in).
 */
export function buildCommitTuples(
	readyFiles: { fileId: string; classification: ClassificationTuple }[],
): ClassifyTuple[] {
	return readyFiles.flatMap(({ fileId, classification }) =>
		Object.entries(classification)
			.filter(([, valueCode]) => valueCode !== '')
			.map(([axisCode, valueCode]) => ({
				objectType: 'file',
				objectId: fileId,
				axisCode,
				valueCode,
			})),
	)
}
